import type { FC, ReactNode } from 'react'
import type { Experience, Profile } from '@/types'

import { useRef } from 'react'

import {
    motion,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionLabel } from '@/components/generals/section-label'
import { Reveal } from '@/components/motion/reveal'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { EASE_OUT } from '@/constants/motion'

interface ExperienceSectionProps {
    profile: Profile | null
    experiences: Experience[]
}

interface TimelineItemProps {
    item: Experience
    index: number
}

const TimelineItem: FC<TimelineItemProps> = ({ item, index }): ReactNode => {
    const isLeft = index % 2 === 0

    return (
        <li className='relative grid pb-14 pl-12 last:pb-0 md:grid-cols-2 md:gap-20 md:pl-0'>
            <motion.span
                aria-hidden='true'
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: '0px 0px -35% 0px' }}
                transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                className='absolute left-0 top-1 flex size-6 items-center justify-center rounded-full border-2 border-primary bg-secondary md:left-1/2 md:-translate-x-1/2'
            >
                <span className='size-2 rounded-full bg-primary' />
            </motion.span>

            <motion.div
                initial={{ opacity: 0, x: isLeft ? -56 : 56 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '0px 0px -20% 0px' }}
                transition={{ duration: 0.9, ease: EASE_OUT }}
                className={cn(isLeft ? 'md:col-start-1 md:text-right' : 'md:col-start-2')}
            >
                {item.period && (
                    <p className='inline-flex rounded-full bg-accent px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-accent-foreground'>
                        <span data-mdlfy-cms={`experiences:${item.id}:period`}>{item.period}</span>
                    </p>
                )}

                <h3 className='mt-4 font-heading text-2xl font-bold leading-tight md:text-3xl'>
                    <span data-mdlfy-cms={`experiences:${item.id}:role`}>{item.role}</span>
                </h3>

                {(item.organization || item.kind) && (
                    <p className={cn('mt-2 flex flex-wrap items-center gap-x-2 gap-y-1', isLeft && 'md:justify-end')}>
                        {item.organization && (
                            <span className='font-semibold' data-mdlfy-cms={`experiences:${item.id}:organization`}>{item.organization}</span>
                        )}

                        {item.organization && item.kind && <span aria-hidden='true'>·</span>}

                        {item.kind && (
                            <span className='font-mono text-xs uppercase tracking-[0.14em]' data-mdlfy-cms={`experiences:${item.id}:kind`}>{item.kind}</span>
                        )}
                    </p>
                )}

                {item.description && (
                    <p
                        data-mdlfy-cms={`experiences:${item.id}:description`}
                        className={cn('mt-3 max-w-md leading-relaxed', isLeft && 'md:ml-auto')}
                    >
                        {item.description}
                    </p>
                )}
            </motion.div>
        </li>
    )
}

// Teal timeline: the orange line draws itself down the page as you scroll and
// each step slides in from its side.
export const ExperienceSection: FC<ExperienceSectionProps> = ({ profile, experiences }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const timelineRef = useRef<HTMLOListElement>(null)
    const reduced = usePrefersReducedMotion()

    const { scrollYProgress } = useScroll({ target: timelineRef, offset: ['start 0.7', 'end 0.6'] })
    const fill = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 })
    const lineScale = useTransform(fill, value => reduced ? 1 : value)

    const { scrollYProgress: sectionProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] })
    const wordX = useTransform(sectionProgress, [0, 1], reduced ? ['0%', '0%'] : ['10%', '-35%'])

    return (
        <section id='experience' ref={sectionRef} className='relative overflow-hidden bg-secondary py-20 text-secondary-foreground md:py-32'>
            <motion.p
                aria-hidden='true'
                style={{ x: wordX }}
                className='text-outline pointer-events-none absolute left-0 top-8 whitespace-nowrap font-heading text-[clamp(6rem,18vw,16rem)] font-extrabold uppercase leading-none tracking-[-0.04em] opacity-20'
            >
                Experience · Experience
            </motion.p>

            <Container className='relative'>
                <Reveal>
                    <SectionLabel index='03' label='Experience' />
                </Reveal>

                {profile?.experience_title && (
                    <Reveal>
                        <h2 className='mt-8 max-w-3xl font-heading text-[clamp(2.6rem,7vw,6rem)] font-extrabold leading-[0.95] tracking-[-0.035em]'>
                            <span data-mdlfy-cms={`profile:${profile.id}:experience_title`}>{profile.experience_title}</span>
                        </h2>
                    </Reveal>
                )}

                <div className={cn('relative', experiences.length > 0 && 'mt-14 md:mt-20')}>
                    {experiences.length > 0 && (
                        <>
                            <span aria-hidden='true' className='absolute bottom-2 left-[11px] top-2 w-0.5 rounded-full bg-white/25 md:left-1/2 md:-translate-x-1/2' />

                            <motion.span
                                aria-hidden='true'
                                style={{ scaleY: lineScale }}
                                className='absolute bottom-2 left-[11px] top-2 w-0.5 origin-top rounded-full bg-primary md:left-1/2 md:-translate-x-1/2'
                            />
                        </>
                    )}

                    {/* Always rendered: the scroll tracker needs this element on the page. */}
                    <ol ref={timelineRef} className='relative'>
                        {experiences.map((item, index) => (
                            <TimelineItem key={item.id} item={item} index={index} />
                        ))}
                    </ol>
                </div>
            </Container>
        </section>
    )
}
